import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import CourseOverview from './CourseOverview';
import CourseComment from './CourseComment';
import CourseRating from './CourseRating';
import CourseNotes from './CourseNotes';
import { saveRecentVideo } from '../../../../services/userService';
import SecureVideoPlayer from '../../../../components/common/SecureVideoPlayer';

function VideoPlayer({ videoUrl, contentData, courseId, onVideoProgress, mentorId, currentLessonIndex, totalLessons, onNextLesson }) {
  const playerRef = useRef(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [key, setKey] = useState(0);
  const [hasReached80Percent, setHasReached80Percent] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [watchTime, setWatchTime] = useState(0);
  const currentVideoIdRef = useRef(null);
  const currentVideoDataRef = useRef(null);
  const watchTimerRef = useRef(null);

  const isYouTubeVideo = videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be');

  // Get user info from localStorage
  const getUserInfo = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const name = user.name || user.full_name || user.username || '';
        const email = user.email || '';
        return name ? `${name} • ${email}` : email;
      }
    } catch (e) {
      console.error('Error parsing user data:', e);
    }
    return 'Protected Content';
  };
  const userInfo = getUserInfo();

  // Reset progress tracking when content changes
  useEffect(() => {
    currentVideoIdRef.current = contentData?.id;
    currentVideoDataRef.current = contentData;
    setActiveTab('overview');
    setKey(prevKey => prevKey + 1);
    setHasReached80Percent(false);
    setIsCompleted(contentData?.completed === 1);
    setWatchTime(0);
  }, [contentData?.id]);

  // Time-based tracking for YouTube videos
  useEffect(() => {
    if (isYouTubeVideo && !isCompleted) {
      watchTimerRef.current = setInterval(() => {
        setWatchTime(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (watchTimerRef.current) {
        clearInterval(watchTimerRef.current);
      }
    };
  }, [isYouTubeVideo, isCompleted]);

  // Mark complete when watch time reaches 80% of duration
  useEffect(() => {
    if (isYouTubeVideo && !isCompleted && contentData?.duration && watchTime > 0) {
      const durationParts = contentData.duration.split(':');
      let totalSeconds = 0;
      if (durationParts.length === 2) {
        totalSeconds = parseInt(durationParts[0]) * 60 + parseInt(durationParts[1]);
      } else if (durationParts.length === 3) {
        totalSeconds = parseInt(durationParts[0]) * 3600 + parseInt(durationParts[1]) * 60 + parseInt(durationParts[2]);
      }
      
      const requiredWatchTime = Math.max(Math.floor(totalSeconds * 0.8), 60);

      if (watchTime >= requiredWatchTime) {
        saveVideoProgressWithData(currentVideoDataRef.current, '100:00', true);
      }
    }
  }, [watchTime, isYouTubeVideo, isCompleted, contentData?.duration]);

  // Function to save video progress
  const saveVideoProgressWithData = useCallback(async (videoData, duration, forceComplete = false) => {
    if (!videoData?.id) return;

    try {
      const jsonData = {
        video_detail_id: videoData.id,
        completed_stream: duration || '0:00',
        is_complete: forceComplete ? 1 : undefined
      };
      await saveRecentVideo(jsonData);
      if (onVideoProgress) {
        onVideoProgress(videoData.id);
      }
      if (forceComplete) {
        setIsCompleted(true);
      }
    } catch (error) {
      console.error('Error saving video progress:', error);
    }
  }, [onVideoProgress]);

  // Format time from seconds to mm:ss
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Handle video progress updates
  const handleVideoProgress = useCallback((progress, currentTime, duration) => {
    if (duration > 0) {
      const formattedTime = formatTime(currentTime);
      if (progress >= 80 && !hasReached80Percent) {
        setHasReached80Percent(true);

        if (currentVideoDataRef.current?.id) {
          saveVideoProgressWithData(currentVideoDataRef.current, formattedTime, true);
        }
      }

      // Save progress at completion
      if (progress >= 95) {
        if (currentVideoDataRef.current?.id) {
          saveVideoProgressWithData(currentVideoDataRef.current, formattedTime, true);
        }
      }
    }
  }, [hasReached80Percent, saveVideoProgressWithData]);

  const handleNextLessonClick = useCallback(async () => {
    if (typeof onNextLesson !== 'function') return;

    try {
      if (currentVideoDataRef.current?.id) {
        await saveVideoProgressWithData(currentVideoDataRef.current, '100:00', true);
      }
    } finally {
      onNextLesson();
    }
  }, [onNextLesson, saveVideoProgressWithData]);

  const handleMarkAsCompleteClick = useCallback(async () => {
    if (isCompleted) return;
    if (!currentVideoDataRef.current?.id) return;
    await saveVideoProgressWithData(currentVideoDataRef.current, '100:00', true);
  }, [isCompleted, saveVideoProgressWithData]);

  return (
    <>
      <div className="rounded-t-2xl overflow-hidden bg-black" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
        <div className="aspect-video w-full">
          <SecureVideoPlayer
            ref={playerRef}
            videoUrl={videoUrl}
            onProgress={handleVideoProgress}
            className="course-video-player"
            autoplay={false}
            controls={true}
            fluid={true}
            responsive={true}
            userInfo={userInfo}
          />
        </div>
      </div>

      <div className="bg-white border-t border-gray-200 px-8 py-5">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-2xl font-semibold text-gray-900">{contentData?.title}</h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">Lesson <span className="font-semibold text-gray-900">{currentLessonIndex}</span> of {totalLessons}</span>
            {typeof onNextLesson === 'function' && totalLessons > 0 && (
              isYouTubeVideo ? (
                <button
                  type="button"
                  onClick={isCompleted ? handleNextLessonClick : handleMarkAsCompleteClick}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${
                    isCompleted ? 'bg-[#7083FF] hover:bg-[#5f73e6]' : 'bg-[#16a34a] hover:bg-[#15803d]'
                  }`}
                >
                  {isCompleted
                    ? (currentLessonIndex < totalLessons ? 'Next Lesson' : 'Explore Courses')
                    : 'Mark as complete'}
                  <Icon icon={isCompleted ? 'mdi:arrow-right' : 'mdi:check'} width="18" height="18" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={isCompleted ? handleNextLessonClick : handleMarkAsCompleteClick}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${
                    isCompleted ? 'bg-[#7083FF] hover:bg-[#5f73e6]' : 'bg-[#16a34a] hover:bg-[#15803d]'
                  }`}
                >
                  {isCompleted
                    ? (currentLessonIndex < totalLessons ? 'Next Lesson' : 'Explore Courses')
                    : 'Mark as complete'}
                  <Icon icon={isCompleted ? 'mdi:arrow-right' : 'mdi:check'} width="18" height="18" />
                </button>
              )
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border-t border-gray-200">
        <div className="px-8 pt-6 pb-4">
          <ul className="flex flex-wrap gap-3.5" role="tablist">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'notes', label: 'Notes' },
              { id: 'comments', label: 'Comments' },
              { id: 'ratings', label: 'Review' },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <li key={tab.id}>
                  <button
                    type="button"
                    className={`px-6 py-3 text-lg font-medium rounded-lg border transition-all ${
                      isActive
                        ? 'border-[#7083FF] text-[#7083FF] bg-[#7083FF]/5'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                    role="tab"
                    aria-selected={isActive}
                  >
                    {tab.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="px-8 py-8 min-h-[180px] rounded-b-2xl">
          {activeTab === 'overview' && (
            <div role="tabpanel" aria-labelledby="tab-overview">
              <CourseOverview key={`overview-${key}`} contentData={contentData} />
            </div>
          )}
          {activeTab === 'notes' && (
            <div role="tabpanel" aria-labelledby="tab-notes">
              <CourseNotes key={`notes-${key}`} contentData={contentData} player={playerRef.current?.player} />
            </div>
          )}
          {activeTab === 'comments' && (
            <div role="tabpanel" aria-labelledby="tab-comments">
              <CourseComment key={`comments-${key}`} contentData={contentData} player={playerRef.current?.player} mentorId={mentorId} />
            </div>
          )}
          {activeTab === 'ratings' && (
            <div role="tabpanel" aria-labelledby="tab-ratings">
              <CourseRating key={`ratings-${key}`} contentData={{ ...contentData, course_id: courseId }} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default VideoPlayer;
